/* La Caza Cali — collection-card light scenes + swatch clicks
 * (round 3: homepage cards, sections/lacaza-collection-block.liquid;
 *  round 4: extended to the standard collection-grid card,
 *  snippets/product-card.liquid — same [data-pcard-root]/
 *  [data-pcard-variants] contract, shared here so the behavior is
 *  defined once instead of twice)
 *
 * Each card with finish/color/style swatches carries a hidden JSON map
 * (variant id -> { image, alt, on?, off?, onAlt?, offAlt? }) in a
 * <script data-pcard-variants> tag. Selecting a swatch looks up the
 * variant in that map:
 *   - if it has an on/off pair, the card media box shows that scene
 *     (two stacked .lp__img elements, reusing the shared light-preview
 *     CSS/crossfade — the page-level master switch drives them the same
 *     way it drives the PDP's [data-light-preview] component)
 *   - otherwise the card falls back to that variant's own product image
 *     (homepage cards only — the standard card's own media stays as-is
 *     since [data-pcard-img] isn't wired into its more complex video/
 *     hover-image markup; the scene overlay simply hides, revealing
 *     whatever was already there)
 *
 * One card, one scene: the on/off pair and the plain image are mutually
 * exclusive (toggled via the native `hidden` attribute), so a card
 * never shows two finishes' imagery at once. No per-card switch — the
 * only control is the swatch row plus the page-level master switch.
 *
 * Two swatch markups are supported so this one file covers both card
 * types:
 *   - [data-pcard-swatch] <button> (homepage cards) — click
 *   - product-variant-options.liquid's <input type="radio"
 *     data-variant-id> (standard card's color swatches) — change
 */
(function () {
  'use strict';

  function bindCard(card) {
    if (!card || card.dataset.pcardBound === '1') return;
    card.dataset.pcardBound = '1';

    var dataEl = card.querySelector('[data-pcard-variants]');
    if (!dataEl) return;

    var map = {};
    try { map = JSON.parse(dataEl.textContent || '{}'); } catch (e) { map = {}; }

    var swatches = card.querySelectorAll('[data-pcard-swatch]');
    var radioSwatches = card.querySelectorAll('input[type="radio"][data-variant-id]');
    if (!swatches.length && !radioSwatches.length) return;

    var imgNormal = card.querySelector('[data-pcard-img]');
    var imgOn = card.querySelector('.lp__img--on');
    var imgOff = card.querySelector('.lp__img--off');
    // Round 5.5 owner fix: the card's own root is no longer the <a>
    // itself (the swatch row moved OUT of the anchor to fix the real-
    // iPhone nested-interactive-content tap bug) — find the link
    // explicitly instead of assuming card.tagName === 'A'. Falls back
    // to `card` itself for any card structure that still IS an anchor
    // (e.g. snippets/product-card.liquid's root, which this file also
    // binds, doesn't use this pattern at all — harmless no-op there).
    var link = card.querySelector('[data-pcard-link]') || (card.tagName === 'A' ? card : null);

    function applyVariant(id) {
      var data = map[String(id)];
      if (!data) return;

      if (data.on && data.off && imgOn && imgOff) {
        // The card <img>s carry a srcset (responsive 400/600/900 candidates);
        // a browser keeps using srcset when only `src` changes, so the
        // picture never swapped (live-site verifier, Sept 20). Point srcset
        // at the new file too.
        imgOn.srcset = data.on;
        imgOn.src = data.on;
        if (data.onAlt) imgOn.alt = data.onAlt;
        imgOff.srcset = data.off;
        imgOff.src = data.off;
        if (data.offAlt) imgOff.alt = data.offAlt;
        imgOn.hidden = false;
        imgOff.hidden = false;
        if (imgNormal) imgNormal.hidden = true;
      } else {
        if (imgOn) imgOn.hidden = true;
        if (imgOff) imgOff.hidden = true;
        if (imgNormal && data.image) {
          if (imgNormal.hasAttribute('srcset')) imgNormal.srcset = data.image;
          imgNormal.src = data.image;
          if (data.alt) imgNormal.alt = data.alt;
          imgNormal.hidden = false;
        }
      }

      // Keep the card's link pointed at the selected variant.
      if (link && link.getAttribute('href')) {
        try {
          var url = new URL(link.getAttribute('href'), window.location.origin);
          url.searchParams.set('variant', id);
          link.setAttribute('href', url.pathname + url.search);
        } catch (e) {}
      }
    }

    function selectSwatch(sw) {
      var id = sw.dataset.variantId;
      if (!id) return;
      var pos = sw.dataset.optionPosition;
      card.querySelectorAll('[data-pcard-swatch][data-option-position="' + pos + '"]').forEach(function (s) {
        s.classList.toggle('is-active', s === sw);
      });
      applyVariant(id);
      updateFinishLabel(sw);
    }

    // "GOLD · 2 finishes" — the finish name is the only part that
    // changes on tap; the " · N finishes" count is server-rendered and
    // stays put. Same swatch row can appear more than once per card
    // markup in theory (it doesn't today), so scope the label lookup to
    // this swatch's own .lacaza-pcard__finishes container.
    function updateFinishLabel(sw) {
      var name = sw.dataset.finishName || sw.getAttribute('aria-label');
      if (!name) return;
      var finishesBlock = sw.closest('.lacaza-pcard__finishes');
      var label = finishesBlock && finishesBlock.querySelector('[data-pcard-finish-name]');
      if (label) label.textContent = name.toUpperCase();
    }

    swatches.forEach(function (sw) {
      // Round 5.3 tried a touchend hack here for the real-iPhone tap
      // failure; round 5.5 found and fixed the actual root cause
      // instead (this <button> was nested inside the card's <a> —
      // invalid HTML, unreliable iOS tap-to-click for the inner
      // element — see the swatch-row-is-now-a-sibling comment in
      // sections/lacaza-collection-block.liquid). Plain click handler.
      // Hover previews the finish name in the "GOLD · 2 finishes" line
      // (replaces the old floating tooltip, which clipped at card edges).
      sw.addEventListener('mouseenter', function () { updateFinishLabel(sw); });
      sw.addEventListener('mouseleave', function () {
        var active = card.querySelector('[data-pcard-swatch][data-option-position="' + sw.dataset.optionPosition + '"].is-active');
        if (active) updateFinishLabel(active);
      });
      sw.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        selectSwatch(sw);
      });
      // Stop Space/Enter (native click on <button>) from bubbling to
      // any parent gallery/keyboard handler, same pattern as the
      // shared light-preview switch.
      sw.addEventListener('keydown', function (e) {
        if (e.key === ' ' || e.key === 'Enter' || e.key === 'Spacebar') {
          e.stopPropagation();
        }
      });
    });

    // Standard collection-grid card (snippets/product-card.liquid) uses
    // native radio inputs for its color swatches (product-variant-
    // options.liquid), not [data-pcard-swatch] buttons — same map,
    // same applyVariant, just a `change` event instead of `click`.
    radioSwatches.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        var id = radio.dataset.variantId;
        if (id) applyVariant(id);
      });
    });
  }

  function init() {
    document.querySelectorAll('[data-pcard-root]').forEach(bindCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Public API: re-run binding for cards injected after the initial page
  // load (load-more / infinite-scroll pagination, assets/load-more.js).
  // bindCard() itself is idempotent (dataset.pcardBound guard), so this
  // is safe to call repeatedly across the whole document.
  window.LacazaCollectionCard = window.LacazaCollectionCard || {};
  window.LacazaCollectionCard.init = init;
})();
